// React hook for organisation and member management operations
// Implements organisation queries and member mutations with authentication, logging, and error handling
// Follows the same pattern as useProjects.ts

import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { logger } from "../services/logger";
import { generateUUID } from "../utils/uuid";
import { useAppStore } from "../stores/appStore";
import {
  getOrganisation,
  listOrganisationMembers,
  updateOrganisationMember,
  getMemberStatusFilters,
} from "../services/organisationApi";
import { getResponseStatus } from "../utils/errorUtils";

/**
 * Hook for organisation and member management operations
 * Fetches organisation details and members, provides member update mutation
 */
export const useOrganisation = (organisationId: string | undefined) => {
  const { user } = useAuth();
  const { setError, clearError } = useAppStore();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [page, setPage] = useState<number>(1);

  // Reset page to 1 when status filter changes
  const handleSetStatusFilter = useCallback((filter: string | undefined) => {
    setStatusFilter(filter);
    setPage(1);
  }, []);

  // ============================================================================
  // ORGANISATION QUERY - Fetch organisation details
  // ============================================================================

  const organisationQuery = useQuery({
    queryKey: ["organisation", organisationId],
    queryFn: async () => {
      const correlationId = generateUUID();

      logger.info("Fetching organisation details", {
        userId: user?.id,
        organisationId,
        correlationId,
      });

      try {
        clearError();

        const data = await getOrganisation(organisationId!);

        logger.info("Organisation fetched successfully", {
          userId: user?.id,
          organisationId,
          organisationName: data.name,
          correlationId,
        });

        return data;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch organisation", error as Error, {
          userId: user?.id,
          organisationId,
          correlationId,
        });

        setError(`Failed to fetch organisation: ${errorMessage}`);
        throw error;
      }
    },
    enabled: !!user && !!organisationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // ============================================================================
  // MEMBER STATUS FILTERS QUERY - Fetch available filter options from DB
  // ============================================================================

  const statusFiltersQuery = useQuery({
    queryKey: ["member-status-filters", organisationId],
    queryFn: async () => {
      const correlationId = generateUUID();

      logger.info("Fetching member status filters", {
        userId: user?.id,
        organisationId,
        correlationId,
      });

      try {
        const data = await getMemberStatusFilters(organisationId!);

        logger.info("Member status filters fetched successfully", {
          userId: user?.id,
          organisationId,
          filterCount: data.length,
          correlationId,
        });

        return data;
      } catch (error) {
        logger.error("Failed to fetch member status filters", error as Error, {
          userId: user?.id,
          organisationId,
          correlationId,
        });
        throw error;
      }
    },
    enabled: !!user && !!organisationId,
    staleTime: 30 * 60 * 1000, // 30 minutes — rarely changes
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // ============================================================================
  // MEMBERS QUERY - Fetch organisation members with pagination + status filter
  // ============================================================================

  const membersQuery = useQuery({
    queryKey: ["organisation-members", organisationId, statusFilter, page],
    queryFn: async () => {
      const correlationId = generateUUID();

      logger.info("Fetching organisation members", {
        userId: user?.id,
        organisationId,
        statusFilter,
        page,
        correlationId,
      });

      try {
        clearError();

        const result = await listOrganisationMembers(organisationId!, {
          statusFilter,
          pageNumber: page,
        });

        logger.info("Organisation members fetched successfully", {
          userId: user?.id,
          organisationId,
          memberCount: result.members.length,
          totalCount: result.pagination?.totalResults,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to fetch organisation members", error as Error, {
          userId: user?.id,
          organisationId,
          correlationId,
        });

        setError(`Failed to fetch organisation members: ${errorMessage}`);
        throw error;
      }
    },
    enabled: !!user && !!organisationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // ============================================================================
  // MEMBER MUTATIONS
  // ============================================================================

  const updateMemberMutation = useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: { role_id?: string; status_id?: number };
    }) => {
      const correlationId = generateUUID();

      logger.info("Updating organisation member", {
        userId: user?.id,
        organisationId,
        targetUserId: userId,
        correlationId,
      });

      try {
        clearError();

        const result = await updateOrganisationMember(
          organisationId!,
          userId,
          updates,
        );

        logger.info("Organisation member updated successfully", {
          userId: user?.id,
          organisationId,
          targetUserId: userId,
          correlationId,
        });

        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";

        logger.error("Failed to update organisation member", error as Error, {
          userId: user?.id,
          organisationId,
          targetUserId: userId,
          correlationId,
        });

        setError(`Failed to update organisation member: ${errorMessage}`);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organisation-members", organisationId],
      });
    },
  });

  // ============================================================================
  // API WRAPPER FUNCTIONS
  // ============================================================================

  const updateMember = async (
    userId: string,
    updates: { role_id?: string; status_id?: number },
  ) => {
    return updateMemberMutation.mutateAsync({ userId, updates });
  };

  // ============================================================================
  // RETURN DATA + MUTATIONS
  // ============================================================================

  return {
    // Query data
    organisation: organisationQuery.data ?? null,
    members: membersQuery.data?.members ?? [],
    pagination: membersQuery.data?.pagination ?? null,
    memberStatusFilters: statusFiltersQuery.data ?? [],

    // Loading states
    isLoadingOrganisation: organisationQuery.isLoading,
    isLoadingMembers: membersQuery.isLoading,
    isLoadingStatusFilters: statusFiltersQuery.isLoading,

    // Status filter (slug-based)
    statusFilter,
    setStatusFilter: handleSetStatusFilter,

    // Pagination
    page,
    setPage,

    // Mutation wrappers
    updateMember,
    isUpdatingMember: updateMemberMutation.isPending,

    // Refetch functions
    refetchOrganisation: organisationQuery.refetch,
    refetchMembers: membersQuery.refetch,
  };
};
